import { card, classChip, emptyNote, sectionTitle } from "../components/ui.js";
import { B, staffOf } from "../domain/context.js";
import { avatar } from "../lib/avatar.js";
import { esc, fmtDate } from "../lib/fmt.js";
import { alerjiler, hayatiRisk, saglik, saglikTakipli } from "../domain/students.js";
import {
  OLAY_TURU,
  acilIlaclar,
  aktifIlaclar,
  bugunkuOlaylar,
  bugunkuUygulamalar,
  ilacSuresi,
  izinsizIlaclar,
  olayPersoneli,
  onaysizOlaylar,
  saglikOzeti,
  sonOlaylar,
  suresiSorunluIlaclar,
} from "../domain/health.js";

// ---------- SAĞLIK ----------
export function viewSaglik() {
  const t = B();
  const o = saglikOzeti(t.id);
  const takipli = saglikTakipli(t.students);

  return `
  ${uyariSeridi(t)}

  <div class="grid gap-3 sm:gap-4 grid-cols-2 xl:grid-cols-4 mb-4">
    ${[
      { label: "Sağlık Takipli", value: takipli.length, sub: `${o.toplamOgrenci} öğrenci içinde`, color: "coral" },
      { label: "Acil İlaç", value: o.acilIlac, sub: "EpiPen vb. — konumu bilinmeli", color: "sun" },
      { label: "Bugün Uygulanan", value: o.bugunUygulama, sub: "doz kaydı", color: "teal" },
      { label: "Bugünkü Olay", value: o.bugunOlay, sub: o.bildirilmemis ? `${o.bildirilmemis} veliye bildirilmedi` : "hepsi bildirildi", color: "sky" },
    ].map(s => card(`
      <div class="p-4">
        <div class="text-xs font-semibold text-inksoft">${s.label}</div>
        <div class="font-display font-extrabold text-3xl mt-1 text-${s.color}">${s.value}</div>
        <div class="text-[11px] text-inksoft/80 mt-0.5">${s.sub}</div>
      </div>`, "min-w-0")).join("")}
  </div>

  <div class="grid gap-4 lg:grid-cols-2 items-start">
    ${acilMudahaleKarti(t)}
    ${alerjenKarti(takipli)}
    ${ilacKarti(t)}
    ${olayKarti(t)}
  </div>`;
}

// ---------- Uyarı şeridi ----------
// Süresi dolmuş EpiPen ya da izinsiz ilaç varsa en üstte durur.
function uyariSeridi(t) {
  const sorunlu = suresiSorunluIlaclar(t.id);
  const izinsiz = izinsizIlaclar(t.id);
  const onaysiz = onaysizOlaylar(t.id);
  if (!sorunlu.length && !izinsiz.length && !onaysiz.length) return "";

  const satir = (renk, metin) =>
    `<div class="rounded-xl bg-${renk}-soft text-${renk} text-sm font-semibold px-4 py-3 mb-2">${metin}</div>`;

  return `<div class="mb-4">
    ${sorunlu.map(m => {
      const s = ilacSuresi(m);
      return satir("coral",
        `${esc(m.drugName)} — ${esc(stuAdi(t, m.studentId))} · ` +
        (s.durum === "doldu"
          ? `SON KULLANMA GEÇTİ (${fmtDate(m.expiresAt)}) — yenisi alınmalı`
          : `${s.kalanGun} gün sonra doluyor (${fmtDate(m.expiresAt)})`));
    }).join("")}
    ${izinsiz.map(m => satir("coral",
      `${esc(m.drugName)} — veli izni kayıtlı değil, uygulanmamalı`)).join("")}
    ${onaysiz.length ? satir("sun",
      `${onaysiz.length} olay raporunda veli okudu onayı bekleniyor`) : ""}
  </div>`;
}

const stuAdi = (t, id) => t.students.find(s => s.id === id)?.name ?? "—";

// ---------- Acil müdahale ----------
function acilMudahaleKarti(t) {
  const acil = acilIlaclar(t.id);
  const riskli = t.students.filter(s => hayatiRisk(s));

  return card(`
    <div class="p-5">
      ${sectionTitle("Acil Müdahale", `<span class="text-[11px] font-bold rounded-full bg-coral text-white px-2.5 py-1">${riskli.length} çocuk</span>`)}
      <p class="text-[11px] text-inksoft mb-3">
        Hayati risk taşıyan çocuklar ve ilaçlarının nerede durduğu. Tüm personel bilmelidir.
      </p>

      ${acil.length ? acil.map(m => {
        const s = t.students.find(x => x.id === m.studentId);
        const sure = ilacSuresi(m);
        return `
        <div class="rounded-xl border-2 border-coral bg-coral-soft p-3 mb-2">
          <div class="flex items-center gap-2">
            ${avatar(s, "w-8 h-8")}
            <span class="min-w-0 flex-1">
              <span class="block text-sm font-semibold truncate">${esc(s.name)}</span>
              <span class="block text-[11px] text-coral font-semibold">${esc(m.indication)}</span>
            </span>
            ${classChip(s.classId)}
          </div>
          <div class="text-xs font-semibold mt-2">${esc(m.drugName)} · ${esc(m.dose)}</div>
          <div class="text-xs mt-1 leading-snug">${esc(m.instructions)}</div>
          <div class="flex items-center justify-between gap-2 text-[11px] mt-2 pt-2 border-t border-coral/20">
            <span>📍 ${esc(m.storage)}</span>
            <span class="shrink-0 font-semibold ${sure.durum === "gecerli" ? "text-inksoft" : "text-coral"}">
              SKT ${fmtDate(m.expiresAt)}
            </span>
          </div>
        </div>`;
      }).join("") : emptyNote("Acil müdahale ilacı kayıtlı değil.")}
    </div>`, "min-w-0");
}

// ---------- Alerjen panosu ----------
function alerjenKarti(takipli) {
  const sirali = [...takipli].sort((a, b) => Number(hayatiRisk(b)) - Number(hayatiRisk(a)));

  return card(`
    <div class="p-5">
      ${sectionTitle("Alerjen & Tanı Panosu", `<span class="text-[11px] text-inksoft hidden sm:block">Mutfak panosunun dijital karşılığı</span>`)}
      ${sirali.length ? `
        <div class="space-y-2">
          ${sirali.map(s => {
            const list = alerjiler(s);
            const m = saglik(s);
            return `
            <button data-student="${s.id}" class="w-full min-w-0 flex items-start gap-3 rounded-xl border ${hayatiRisk(s) ? "border-coral" : "border-line"} px-3 py-2.5 text-left hover:bg-paper/60">
              ${avatar(s, "w-8 h-8")}
              <span class="min-w-0 flex-1">
                <span class="block text-sm font-semibold truncate">${esc(s.name)}</span>
                <span class="block text-[11px]">
                  ${list.map(a => `<span class="${a.severity === "anafilaksi" || a.severity === "agir" ? "text-coral font-bold" : "text-inksoft"}">${esc(a.name)}${a.severity === "anafilaksi" ? " ⚠" : ""}</span>`).join(", ")}
                  ${m?.conditions?.length ? `${list.length ? " · " : ""}<span class="text-inksoft">${m.conditions.map(esc).join(", ")}</span>` : ""}
                </span>
              </span>
              ${classChip(s.classId)}
            </button>`;
          }).join("")}
        </div>` : emptyNote("Sağlık takibi gereken çocuk yok.")}
    </div>`, "min-w-0");
}

// ---------- İlaç kayıtları ----------
function ilacKarti(t) {
  const list = aktifIlaclar(t.id).filter(m => m.schedule !== "acil");
  const bugun = bugunkuUygulamalar(t.id);

  return card(`
    <div class="p-5">
      ${sectionTitle("İlaç Kayıtları", `<span class="text-[11px] text-inksoft">${bugun.length} doz bugün</span>`)}

      ${list.length ? list.map(m => {
        const s = t.students.find(x => x.id === m.studentId);
        const son = (m.administrations ?? []).at(-1);
        return `
        <div class="rounded-xl border border-line p-3 mb-2">
          <div class="flex items-center gap-2">
            ${avatar(s, "w-7 h-7")}
            <span class="min-w-0 flex-1">
              <span class="block text-sm font-semibold truncate">${esc(s.name)}</span>
              <span class="block text-[11px] text-inksoft truncate">${esc(m.drugName)} · ${esc(m.dose)}</span>
            </span>
            <span class="text-[10px] font-bold rounded-full bg-teal-soft text-teal px-2 py-0.5 shrink-0">
              ${m.schedule === "gunluk" ? "Günlük" : "Gerektikçe"}
            </span>
          </div>

          <div class="text-xs mt-1.5 leading-snug">${esc(m.instructions)}</div>

          <div class="flex items-center gap-2 text-[11px] text-inksoft mt-2 pt-2 border-t border-line/70">
            <span class="${m.consent?.at ? "text-teal" : "text-coral font-bold"}">
              ${m.consent?.at ? `✓ Veli izni ${fmtDate(m.consent.at)} — ${esc(m.consent.byGuardian)}` : "✕ Veli izni YOK"}
            </span>
          </div>

          ${son ? `
            <div class="text-[11px] text-inksoft mt-1">
              Son doz: ${esc(son.at.slice(0, 10))} ${esc(son.at.slice(11))} ·
              ${esc(staffOf(son.staffId)?.name ?? "—")}${son.note ? ` · ${esc(son.note)}` : ""}
            </div>` : `<div class="text-[11px] text-inksoft mt-1">Henüz uygulanmadı.</div>`}

          <button data-givemed="${m.id}"
            class="w-full mt-2 rounded-lg bg-ink text-white text-[11px] font-semibold py-1.5 hover:bg-inksoft transition disabled:opacity-40"
            ${m.consent?.at ? "" : "disabled title='Veli izni olmadan ilaç verilemez'"}>
            Doz uygulandı olarak kaydet
          </button>
        </div>`;
      }).join("") : emptyNote("Aktif ilaç kaydı yok.")}
    </div>`, "min-w-0");
}

// ---------- Olay kayıtları ----------
function olayKarti(t) {
  const list = sonOlaylar(t.id, 45);
  const bugun = bugunkuOlaylar(t.id);

  return card(`
    <div class="p-5">
      ${sectionTitle(
        "Olay & Kaza Kayıtları",
        `<button data-newincident class="text-[11px] font-bold rounded-lg bg-ink text-white px-2.5 py-1 hover:bg-inksoft transition">+ Olay kaydet</button>`,
      )}
      <p class="text-[11px] text-inksoft mb-3">
        Son 45 gün · ${list.length} kayıt${bugun.length ? ` · bugün ${bugun.length}` : ""}.
        Kayıtlar silinemez.
      </p>

      ${list.length ? `
        <ol class="space-y-2">
          ${list.map(o => {
            const s = t.students.find(x => x.id === o.studentId);
            const tur = OLAY_TURU[o.type] ?? OLAY_TURU.diger;
            return `
            <li class="rounded-xl border border-line p-3">
              <div class="flex items-center gap-2">
                <span class="shrink-0">${tur.icon}</span>
                <span class="min-w-0 flex-1">
                  <span class="block text-sm font-semibold truncate">${esc(s?.name ?? "—")} · ${esc(tur.label)}</span>
                  <span class="block text-[11px] text-inksoft">${fmtDate(o.at.slice(0, 10))} ${esc(o.at.slice(11))} · ${esc(o.location)}</span>
                </span>
                ${o.parentAckAt
                  ? `<span class="text-[10px] font-bold rounded-full bg-teal-soft text-teal px-2 py-0.5 shrink-0">Veli okudu</span>`
                  : o.parentNotifiedAt
                    ? `<span class="text-[10px] font-bold rounded-full bg-sun-soft text-[#B07E14] px-2 py-0.5 shrink-0">Bildirildi</span>`
                    : `<span class="text-[10px] font-bold rounded-full bg-coral text-white px-2 py-0.5 shrink-0">Bildirilmedi</span>`}
              </div>
              <div class="text-xs mt-1.5">${esc(o.description)}</div>
              <div class="text-xs text-inksoft mt-1"><span class="font-semibold">Müdahale:</span> ${esc(o.firstAid)}</div>
              <div class="text-[11px] text-inksoft mt-1.5 pt-1.5 border-t border-line/70">
                Kaydeden: ${esc(olayPersoneli(o))}${o.followUp ? ` · Takip: ${esc(o.followUp)}` : ""}
              </div>
              ${!o.parentNotifiedAt
                ? `<button data-notifyincident="${o.id}" class="w-full mt-2 rounded-lg bg-coral text-white text-[11px] font-semibold py-1.5">Veliye bildirildi olarak işaretle</button>`
                : ""}
            </li>`;
          }).join("")}
        </ol>` : emptyNote("Son 45 günde olay kaydı yok.")}
    </div>`, "min-w-0");
}
